import * as z from "zod";

const timePeriodSchema = z.enum([
  "day",
  "week",
  "month",
  "last-month",
  "quarter",
  "all",
  "custom",
]);

const customRangeSchema = z.object({
  start: z.string().min(1),
  end: z.string().min(1),
});

export const funnelModeSchema = z.enum(["weighted", "fully-onboarded"]);
export type FunnelMode = z.infer<typeof funnelModeSchema>;

export const selfServeOnboardingFunnelOutcomeTypeSchema = z.enum([
  "completed",
  "help-requested",
  "partial-completion",
]);

export const selfServeOnboardingFunnelOutcomeBreakdownDatumSchema = z.object({
  label: z.string().min(1),
  count: z.number().int().min(0),
  percentage: z.number().min(0).max(100),
  outcomeType: selfServeOnboardingFunnelOutcomeTypeSchema,
});

const statusFilterSchema = z.enum([
  "Completed",
  "Reopened",
  "Awaiting approval",
  "In progress",
  "Partial Completion",
  "Help Requested",
]);

const numericFilterSchema = z.object({
  operator: z.enum(["lt", "gt"]),
  value: z.number().min(0),
});

const dateRangeFilterSchema = z.object({
  start: z.string().min(1),
  end: z.string().min(1),
});

export const selfServeOnboardingFunnelStatsSchema = z.object({
  totalStarted: z.number().int().min(0),
  totalCompleted: z.number().int().min(0),
  totalHelpRequested: z.number().int().min(0),
  totalPartialCompletions: z.number().int().min(0),
  totalDropoffs: z.number().int().min(0),
  overallConversion: z.number().min(0).max(100),
  outcomeBreakdown: z.array(
    selfServeOnboardingFunnelOutcomeBreakdownDatumSchema,
  ),
  previousPeriodConversion: z.number().min(0).max(100).optional(),
  conversionChange: z.number().optional(),
  // Previous period changes
  totalStartedChange: z.number().int().optional(),
  totalCompletedChange: z.number().int().optional(),
  totalDropoffsChange: z.number().int().optional(),
});

export const getSelfServeOnboardingFunnelStats = {
  name: "getSelfServeOnboardingFunnelStats" as const,
  input: z
    .object({
      timePeriod: timePeriodSchema,
      customRange: customRangeSchema.optional(),
      funnelMode: funnelModeSchema.optional(),
      // Advanced filters (matching stats/time-series endpoints)
      filterLowIntent: z.boolean().optional(),
      typeFilter: z.enum(["all", "Self-Serve", "Custom"]).optional(),
      statuses: z.array(statusFilterSchema).optional(),
      configurationPushedFilter: z.enum(["yes", "no"]).optional(),
      startDateRange: dateRangeFilterSchema.optional(),
      durationFilter: numericFilterSchema.optional(),
      dealSizeFilter: numericFilterSchema.optional(),
    })
    .superRefine((value, ctx) => {
      if (value.timePeriod === "custom" && !value.customRange) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "customRange is required when timePeriod is custom",
          path: ["customRange"],
        });
      }
    }),
  output: selfServeOnboardingFunnelStatsSchema,
};

export type SelfServeOnboardingFunnelStats = z.infer<
  typeof selfServeOnboardingFunnelStatsSchema
>;
export type SelfServeOnboardingFunnelOutcomeBreakdownDatum = z.infer<
  typeof selfServeOnboardingFunnelOutcomeBreakdownDatumSchema
>;
export type GetSelfServeOnboardingFunnelStatsInput = z.infer<
  typeof getSelfServeOnboardingFunnelStats.input
>;
export type GetSelfServeOnboardingFunnelStatsOutput = z.infer<
  typeof getSelfServeOnboardingFunnelStats.output
>;
